import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { CustomerService } from '../../services/customer.service';
import { CustomerCreateDTO } from '../../interfaces/sales/customer.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SalespersonSelect from './scope/SalespersonSelect';
import { usePermission } from '../../hooks/usePermission';
import { helpWhy } from '../../help/why';
import { PERMISSIONS } from '../../constants/permissions';

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const isEdit = Boolean(id);

  const canPickSalesperson = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [salespersonId, setSalespersonId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [taxRegistrationNo, setTaxRegistrationNo] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const customer = await CustomerService.getById(id!);
        setName(customer.name);
        setContactPerson(customer.contactPerson ?? '');
        setEmail(customer.email ?? '');
        setPhone(customer.phone ?? '');
        setAddress(customer.address ?? '');
        setTaxRegistrationNo(customer.taxRegistrationNo ?? '');
      } catch (e) {
        toast.showApiError(e, 'Failed to load customer');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id]);

  const nameError = name.trim() === '' ? 'Customer name is required' : '';
  const emailError = email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    ? 'Enter a valid email address, e.g. name@company.com' : '';
  const phoneError = phone.trim() !== '' && !/^[+()\-\s\d]{6,20}$/.test(phone.trim())
    ? 'Enter a valid phone number (digits, +, - and spaces only)' : '';

  const handleSubmit = async () => {
    setSubmitted(true);
    if (nameError || emailError || phoneError) return;
    const institutionId = AuthService.getSelectedInstitution()!;
    const dto: CustomerCreateDTO = {
      name: name.trim(),
      contactPerson: contactPerson.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      address: address.trim() || undefined,
      taxRegistrationNo: taxRegistrationNo.trim() || undefined,
      institutionId,
      ...(!isEdit && canPickSalesperson && salespersonId ? { salespersonId } : {}),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await CustomerService.update(id!, dto);
        toast.showSuccess('Customer updated');
        navigate(`/sales/customers/${id}`);
      } else {
        const created = await CustomerService.create(dto);
        toast.showSuccess('Customer created');
        navigate(`/sales/customers/${created.id}`);
      }
    } catch (e) {
      toast.showApiError(e, isEdit ? 'Failed to update customer' : 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.customerForm.page">
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? 'Edit Customer' : 'New Customer'}
      </Typography>

      <Paper data-help-section="sales.customerForm.details" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            data-help="sales.customerForm.field.name"
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            onBlur={() => setSubmitted(true)}
            error={submitted && !!nameError}
            helperText={submitted ? nameError : ''}
          />
          <TextField
            data-help="sales.customerForm.field.contactPerson"
            label="Contact Person"
            value={contactPerson}
            onChange={e => setContactPerson(e.target.value)}
          />
          <TextField
            data-help="sales.customerForm.field.email"
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
          />
          <TextField
            data-help="sales.customerForm.field.phone"
            label="Phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            error={!!phoneError}
            helperText={phoneError}
          />
          <TextField
            data-help="sales.customerForm.field.address"
            label="Address"
            value={address}
            onChange={e => setAddress(e.target.value)}
            multiline
            rows={3}
          />
          <TextField
            data-help="sales.customerForm.field.taxRegistrationNo"
            label="Tax Registration No."
            value={taxRegistrationNo}
            onChange={e => setTaxRegistrationNo(e.target.value)}
          />
          {!isEdit && canPickSalesperson && (
            <SalespersonSelect data-help="sales.customerForm.field.salesperson" size="medium" value={salespersonId} onChange={setSalespersonId} helperText="Leave empty to own this customer yourself." />
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          data-help="sales.customerForm.save"
          {...helpWhy(!saving && name.trim() === '' ? { kind: 'status', message: 'Enter the customer name first.' } : null)}
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || name.trim() === ''}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Customer'}
        </Button>
        <Button data-help="sales.customerForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerForm;
